import {
  Box,
  Heading,
  HStack,
  Icon,
  IconButton,
  Stack,
  useDisclosure,
} from "@chakra-ui/react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FaTimes as CancelIcon } from "react-icons/fa";
import { useApplicationForApplicationBuildQuery } from "../hooks/useApplicationBuildsQuery";
import { SESSION_STATE } from "../session/session-state";
import type { SessionData } from "../session/types";
import { terminateSession } from "../signalR";
import { routes } from "../utils/routes";
import { ApplicationImage } from "./ApplicationImage";
import { BrandedSkeleton } from "./BrandedSkeleton";
import { ConfirmSessionAbortDialog } from "./ConfirmSessionAbortDialog";
import { LinkButton } from "./LinkButton";

import { CircularProgress, Text } from "@chakra-ui/react";
import { useSessionDevice } from "../hooks";
import { SessionType } from "../session/types";
import { ExpectedWaitTime } from "./ExpectedWaitTime";
import { ProgressDisplay } from "./ProgressDisplay";

export function ImageOverlay({
  session: currentSession,
}: {
  session: Pick<SessionData, "state" | "message" | "type" | "progress">;
}) {
  const { t } = useTranslation();
  const device = useSessionDevice();
  const isSessionReady = useMemo(
    () => currentSession.state === SESSION_STATE.READY,
    [currentSession.state],
  );
  const isSessionEnding = useMemo(
    () => currentSession.state === SESSION_STATE.ENDING,
    [currentSession.state],
  );
  const isDesktopSession =
    currentSession.type === SessionType.LocallyRenderedWindows;

  const spinner = (
    <CircularProgress isIndeterminate color="brand.500" size={10} />
  );

  return (
    <>
      {currentSession.state < SESSION_STATE.READY && spinner}
      <Text textAlign={"center"}>
        {isSessionReady &&
          t("session.ready_instructions", {
            device: device?.name ?? "Unknown",
          })}
        {currentSession.state < SESSION_STATE.READY &&
          (isDesktopSession ? (
            <ProgressDisplay progress={currentSession.progress} />
          ) : currentSession.message ===
            "This operation is taking longer than expected" ? (
            <>{t("session.taking_longer_than_expected")}</>
          ) : (
            <>
              {t("session.ready_in")} <ExpectedWaitTime />
            </>
          ))}
        {isSessionEnding && <>{t("session.ending")}</>}
      </Text>
    </>
  );
}

export function ActiveSessionOverview({
  session: currentSession,
  onGotoSession = () => {},
}: {
  session: SessionData;
  onGotoSession(): void;
}) {
  const { data: app } = useApplicationForApplicationBuildQuery(
    currentSession.applicationBuildId,
  );
  const { t } = useTranslation();
  const abortSessionDialogState = useDisclosure();

  const isSessionActive = useMemo(
    () => currentSession.state === SESSION_STATE.ACTIVE,
    [currentSession.state],
  );
  const isSessionEnding = useMemo(
    () => currentSession.state === SESSION_STATE.ENDING,
    [currentSession.state],
  );

  return (
    <>
      <Stack px={2}>
        <BrandedSkeleton isLoaded={!!app}>
          <Heading as="h3" fontSize="lg">
            {app?.name}
          </Heading>
        </BrandedSkeleton>
        <Box position="relative">
          <ApplicationImage
            src={app?.images?.[0]?.image}
            borderRadius={4}
            minHeight={120}
            filter="blur 5px"
          />

          <Stack
            width="100%"
            height="100%"
            justifyContent="center"
            alignItems="center"
            position="absolute"
            top="0"
            left="0"
            bgColor={!isSessionActive ? "backgroundAlpha.800" : undefined}
          >
            <ImageOverlay session={currentSession} />
          </Stack>
        </Box>
        {!isSessionEnding && (
          <HStack>
            <LinkButton
              to={routes.session}
              colorScheme="brand"
              variant="solid"
              width="full"
              onClick={onGotoSession}
              linkProps={{ flexGrow: 1 }}
            >
              {t("session.go_to_session")}
            </LinkButton>
            <IconButton
              aria-label="Abort session"
              icon={<Icon as={CancelIcon} />}
              onClick={abortSessionDialogState.onOpen}
              colorScheme="red"
            />
          </HStack>
        )}
      </Stack>
      <ConfirmSessionAbortDialog
        isOpen={abortSessionDialogState.isOpen}
        onClose={abortSessionDialogState.onClose}
        onConfirm={() => {
          currentSession.id && terminateSession(currentSession.id);
        }}
      />
    </>
  );
}
